import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import {
  BuildingIcon,
  MoveDiagonalIcon,
  PlusIcon,
  StarIcon,
  UsersIcon
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { CreateViewDialog } from '@/components/views/CreateViewDialog';
import { EditViewDialog } from '@/components/views/EditViewDialog';
import { useFieldSelections } from '@/hooks/useFieldSelections';

const GET_USER_SAVED_VIEWS = gql`
  query GetUserSavedViews {
    userSavedViews {
      edges {
        node {
          id
          name
          category
          departmentId
          departmentName
          targetModel
          visibility
          description
          filterJson
          columnsConfig
          recordCount
          createdByName
          ownerName
          createdAt
          lastAccessed
          accessCount
        }
      }
    }
  }
`;

interface View {
  accessCount: number;
  category?: string;
  createdAt: string;
  createdByName: string;
  departmentId?: string;
  departmentName?: string;
  description?: string;
  filterJson?: string;
  id: string;
  lastAccessed?: string;
  name: string;
  ownerName: string;
  recordCount: number;
  targetModel: string;
  visibility: string;
}

interface ViewsData {
  userSavedViews: {
    edges: { node: View }[];
  };
}

const VIEWS_COLUMNS_STORAGE_KEY = 'crm-views-column-widths';
const MIN_VIEW_COLUMN_WIDTH = 120;
const MAX_VIEW_COLUMN_WIDTH = 900;

type ViewColumnField =
  | 'accessCount'
  | 'actions'
  | 'category'
  | 'department'
  | 'name'
  | 'owner'
  | 'records'
  | 'target'
  | 'visibility';

const DEFAULT_VIEW_COLUMN_WIDTHS: Record<ViewColumnField, number> = {
  accessCount: 140,
  actions: 120,
  category: 180,
  department: 180,
  name: 220,
  owner: 180,
  records: 120,
  target: 150,
  visibility: 130
};

export default function ViewsPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedView, setSelectedView] = useState<null | View>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [columnWidths, setColumnWidths] = useState<
    Partial<Record<ViewColumnField, number>>
  >({});
  const resizingFieldRef = useRef<null | ViewColumnField>(null);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(MIN_VIEW_COLUMN_WIDTH);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [search]);

  const { data, error, loading, refetch } =
    useQuery<ViewsData>(GET_USER_SAVED_VIEWS);

  // Fetch field selections from backend for proper label display
  const { getLabel: getTargetModelLabel } = useFieldSelections(
    'opstream.saved.view',
    'target_model'
  );
  const { getLabel: getVisibilityLabel } = useFieldSelections(
    'opstream.saved.view',
    'visibility'
  );

  const views = data?.userSavedViews.edges.map(e => e.node) ?? [];

  const filteredViews = views.filter(view =>
    view.name.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  useEffect(() => {
    setColumnWidths(readColumnWidths());
  }, []);

  useEffect(() => {
    writeColumnWidths(columnWidths);
  }, [columnWidths]);

  const getColumnWidth = useCallback(
    (field: ViewColumnField) =>
      columnWidths[field] ?? DEFAULT_VIEW_COLUMN_WIDTHS[field],
    [columnWidths]
  );

  const handleColumnResizeStart = useCallback(
    (field: ViewColumnField, event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      resizingFieldRef.current = field;
      resizeStartXRef.current = event.clientX;
      resizeStartWidthRef.current = getColumnWidth(field);
    },
    [getColumnWidth]
  );

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const resizingField = resizingFieldRef.current;
      if (!resizingField) {
        return;
      }

      const deltaX = event.clientX - resizeStartXRef.current;
      const nextWidth = clampWidth(resizeStartWidthRef.current + deltaX);
      setColumnWidths(prev => ({
        ...prev,
        [resizingField]: nextWidth
      }));
    };

    const handleMouseUp = () => {
      resizingFieldRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleEditView = (view: View) => {
    setSelectedView(view);
    setIsEditDialogOpen(true);
  };

  const handleViewSaved = () => {
    void refetch();
    setIsCreateDialogOpen(false);
    setIsEditDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] w-full items-center justify-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Filter/Action Bar */}
      <div className="bg-sidebar flex justify-between gap-x-2.5 overflow-y-hidden border-b px-5 py-1.5 md:pl-14">
        <div className="flex items-end gap-x-2.5">
          <div className="w-[300px]">
            <Field className="gap-0">
              <FieldLabel
                className="h-5"
                htmlFor="search-field"
              >
                Search
              </FieldLabel>
              <Input
                id="search-field"
                onChange={e => {
                  setSearch(e.target.value);
                }}
                placeholder="Search views..."
                value={search}
              />
            </Field>
          </div>
        </div>
        <div className="flex items-end gap-x-2.5">
          <Button
            onClick={() => {
              setIsCreateDialogOpen(true);
            }}
          >
            <PlusIcon />
            Create View
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-5 md:px-14 md:py-6">
          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-6">
              <h3 className="font-semibold text-red-800">
                Error loading views
              </h3>
              <p className="mt-2 text-sm text-red-700">{error.message}</p>
            </div>
          ) : filteredViews.length === 0 ? (
            <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <StarIcon className="mb-2 size-12 text-gray-400" />
                <h3 className="text-xl font-semibold">No views found</h3>
                <p className="text-muted-foreground max-w-md text-sm">
                  {debouncedSearch
                    ? 'No views match your search criteria'
                    : 'Create your first saved view to get started'}
                </p>
                {!debouncedSearch && (
                  <Button
                    className="mt-2"
                    onClick={() => {
                      setIsCreateDialogOpen(true);
                    }}
                  >
                    <PlusIcon />
                    Create View
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-0 text-xs font-semibold" />
                  <TableHead
                    className="relative text-xs font-semibold"
                    style={{
                      maxWidth: getColumnWidth('name'),
                      minWidth: getColumnWidth('name'),
                      width: getColumnWidth('name')
                    }}
                  >
                    Name
                    <div
                      className="bg-border/40 absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize touch-none opacity-0 transition-opacity select-none hover:opacity-100"
                      onMouseDown={event => {
                        handleColumnResizeStart('name', event);
                      }}
                    />
                  </TableHead>
                  <TableHead
                    className="relative text-xs font-semibold"
                    style={{
                      maxWidth: getColumnWidth('department'),
                      minWidth: getColumnWidth('department'),
                      width: getColumnWidth('department')
                    }}
                  >
                    Department
                    <div
                      className="bg-border/40 absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize touch-none opacity-0 transition-opacity select-none hover:opacity-100"
                      onMouseDown={event => {
                        handleColumnResizeStart('department', event);
                      }}
                    />
                  </TableHead>
                  <TableHead
                    className="relative text-xs font-semibold"
                    style={{
                      maxWidth: getColumnWidth('category'),
                      minWidth: getColumnWidth('category'),
                      width: getColumnWidth('category')
                    }}
                  >
                    Category
                    <div
                      className="bg-border/40 absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize touch-none opacity-0 transition-opacity select-none hover:opacity-100"
                      onMouseDown={event => {
                        handleColumnResizeStart('category', event);
                      }}
                    />
                  </TableHead>
                  <TableHead
                    className="relative text-xs font-semibold"
                    style={{
                      maxWidth: getColumnWidth('target'),
                      minWidth: getColumnWidth('target'),
                      width: getColumnWidth('target')
                    }}
                  >
                    Target
                    <div
                      className="bg-border/40 absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize touch-none opacity-0 transition-opacity select-none hover:opacity-100"
                      onMouseDown={event => {
                        handleColumnResizeStart('target', event);
                      }}
                    />
                  </TableHead>
                  <TableHead
                    className="relative text-xs font-semibold"
                    style={{
                      maxWidth: getColumnWidth('records'),
                      minWidth: getColumnWidth('records'),
                      width: getColumnWidth('records')
                    }}
                  >
                    Records
                    <div
                      className="bg-border/40 absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize touch-none opacity-0 transition-opacity select-none hover:opacity-100"
                      onMouseDown={event => {
                        handleColumnResizeStart('records', event);
                      }}
                    />
                  </TableHead>
                  <TableHead
                    className="relative text-xs font-semibold"
                    style={{
                      maxWidth: getColumnWidth('visibility'),
                      minWidth: getColumnWidth('visibility'),
                      width: getColumnWidth('visibility')
                    }}
                  >
                    Visibility
                    <div
                      className="bg-border/40 absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize touch-none opacity-0 transition-opacity select-none hover:opacity-100"
                      onMouseDown={event => {
                        handleColumnResizeStart('visibility', event);
                      }}
                    />
                  </TableHead>
                  <TableHead
                    className="relative text-xs font-semibold"
                    style={{
                      maxWidth: getColumnWidth('owner'),
                      minWidth: getColumnWidth('owner'),
                      width: getColumnWidth('owner')
                    }}
                  >
                    Owner
                    <div
                      className="bg-border/40 absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize touch-none opacity-0 transition-opacity select-none hover:opacity-100"
                      onMouseDown={event => {
                        handleColumnResizeStart('owner', event);
                      }}
                    />
                  </TableHead>
                  <TableHead
                    className="relative text-xs font-semibold"
                    style={{
                      maxWidth: getColumnWidth('accessCount'),
                      minWidth: getColumnWidth('accessCount'),
                      width: getColumnWidth('accessCount')
                    }}
                  >
                    Access Count
                    <div
                      className="bg-border/40 absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize touch-none opacity-0 transition-opacity select-none hover:opacity-100"
                      onMouseDown={event => {
                        handleColumnResizeStart('accessCount', event);
                      }}
                    />
                  </TableHead>
                  <TableHead
                    className="relative text-xs font-semibold"
                    style={{
                      maxWidth: getColumnWidth('actions'),
                      minWidth: getColumnWidth('actions'),
                      width: getColumnWidth('actions')
                    }}
                  >
                    Actions
                    <div
                      className="bg-border/40 absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize touch-none opacity-0 transition-opacity select-none hover:opacity-100"
                      onMouseDown={event => {
                        handleColumnResizeStart('actions', event);
                      }}
                    />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredViews.map(view => (
                  <TableRow key={view.id}>
                    <TableCell className="w-0">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            asChild
                            size="icon"
                            variant="ghost"
                          >
                            <Link
                              to={`/firm/crm-${view.targetModel === 'account' ? 'accounts' : 'contacts'}?viewId=${view.id}`}
                            >
                              <MoveDiagonalIcon />
                            </Link>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>View Details</TooltipContent>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      className="max-w-[200px] overflow-hidden font-medium"
                      style={{
                        maxWidth: getColumnWidth('name'),
                        minWidth: getColumnWidth('name'),
                        width: getColumnWidth('name')
                      }}
                    >
                      <span className="block truncate">{view.name}</span>
                    </TableCell>
                    <TableCell
                      className="overflow-hidden"
                      style={{
                        maxWidth: getColumnWidth('department'),
                        minWidth: getColumnWidth('department'),
                        width: getColumnWidth('department')
                      }}
                    >
                      {view.departmentName || (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell
                      className="overflow-hidden"
                      style={{
                        maxWidth: getColumnWidth('category'),
                        minWidth: getColumnWidth('category'),
                        width: getColumnWidth('category')
                      }}
                    >
                      {view.category || (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell
                      style={{
                        maxWidth: getColumnWidth('target'),
                        minWidth: getColumnWidth('target'),
                        width: getColumnWidth('target')
                      }}
                    >
                      <Badge variant="outline">
                        {view.targetModel === 'account' ? (
                          <BuildingIcon className="size-3" />
                        ) : (
                          <UsersIcon className="size-3" />
                        )}
                        {getTargetModelLabel(view.targetModel) || '-'}
                      </Badge>
                    </TableCell>
                    <TableCell
                      style={{
                        maxWidth: getColumnWidth('records'),
                        minWidth: getColumnWidth('records'),
                        width: getColumnWidth('records')
                      }}
                    >
                      {view.recordCount != null
                        ? view.recordCount.toLocaleString()
                        : '-'}
                    </TableCell>
                    <TableCell
                      style={{
                        maxWidth: getColumnWidth('visibility'),
                        minWidth: getColumnWidth('visibility'),
                        width: getColumnWidth('visibility')
                      }}
                    >
                      {getVisibilityLabel(view.visibility) || '-'}
                    </TableCell>
                    <TableCell
                      className="text-gray-600"
                      style={{
                        maxWidth: getColumnWidth('owner'),
                        minWidth: getColumnWidth('owner'),
                        width: getColumnWidth('owner')
                      }}
                    >
                      {view.ownerName || '-'}
                    </TableCell>
                    <TableCell
                      className="text-gray-600"
                      style={{
                        maxWidth: getColumnWidth('accessCount'),
                        minWidth: getColumnWidth('accessCount'),
                        width: getColumnWidth('accessCount')
                      }}
                    >
                      {view.accessCount != null ? view.accessCount : '-'}
                    </TableCell>
                    <TableCell
                      style={{
                        maxWidth: getColumnWidth('actions'),
                        minWidth: getColumnWidth('actions'),
                        width: getColumnWidth('actions')
                      }}
                    >
                      <Button
                        onClick={() => {
                          handleEditView(view);
                        }}
                        size="sm"
                        variant="ghost"
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <CreateViewDialog
        onOpenChange={setIsCreateDialogOpen}
        onViewCreated={handleViewSaved}
        open={isCreateDialogOpen}
      />

      {selectedView && (
        <EditViewDialog
          onOpenChange={setIsEditDialogOpen}
          onViewUpdated={handleViewSaved}
          open={isEditDialogOpen}
          view={selectedView}
        />
      )}
    </div>
  );
}

function clampWidth(value: number) {
  return Math.max(
    MIN_VIEW_COLUMN_WIDTH,
    Math.min(MAX_VIEW_COLUMN_WIDTH, value)
  );
}

function readColumnWidths(): Partial<Record<ViewColumnField, number>> {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const rawValue = window.localStorage.getItem(VIEWS_COLUMNS_STORAGE_KEY);
    if (!rawValue) {
      return {};
    }

    const parsedValue = JSON.parse(rawValue) as Record<string, unknown>;
    const nextValue: Partial<Record<ViewColumnField, number>> = {};

    for (const [field, value] of Object.entries(parsedValue)) {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        continue;
      }

      if (field in DEFAULT_VIEW_COLUMN_WIDTHS) {
        nextValue[field as ViewColumnField] = clampWidth(value);
      }
    }

    return nextValue;
  } catch {
    return {};
  }
}

function writeColumnWidths(
  columnWidths: Partial<Record<ViewColumnField, number>>
) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      VIEWS_COLUMNS_STORAGE_KEY,
      JSON.stringify(columnWidths)
    );
  } catch {
    // Storage access can fail in restricted environments; keep table usable.
  }
}
